/**
 * chat-relay-thread: the thread read contract shared by the API and the webapp.
 * Types only, plus the filter list. Browser-safe (re-exported from browser.ts).
 * Contract: docs/data-model/dm-client-chat.md (Read model); design D1, D3, D6, D7, D9, D10.
 */
import type { ClientIntentType } from '../client-outbox/client-outbox.repository.js';

/** Chips are lenses, not partitions (O6): one entry may show under several. */
export const THREAD_FILTERS = ['all', 'chat', 'automatic', 'forms', 'payments'] as const;
export type ThreadFilter = (typeof THREAD_FILTERS)[number];

export type ThreadChannel = 'telegram' | 'secure';
/**
 * client-chat-screen D1, D4: a room is a channel. The Telegram room is every entry the admin thread
 * merges (chat rows on `telegram`, ledger rows, activity rows); the Secure room is `secure` chat rows only.
 */
export type ThreadRoom = ThreadChannel;
export const THREAD_ROOMS: readonly ThreadRoom[] = ['telegram', 'secure'];
/** Unread per room, as the reading party counts it. */
export type ThreadRoomCounts = Record<ThreadRoom, number>;
/** Derived server-side from the sender (D6); never "self" or "other". */
export type ThreadDirection = 'inbound' | 'outbound';
export type ThreadParseMode = 'HTML' | 'Markdown' | 'MarkdownV2' | 'plain' | null;
/** Ledger rows only (D9): QUEUED with an attempt is `sending`. */
export type ThreadDeliveryState = 'sent' | 'sending' | 'failed';

/** Where tapping an automatic message goes (D7). Null on the payload means not tappable. */
export type ThreadTapTarget =
  /** `/admin/calendar/{kind}/:id` (`adminBookingPagePath`); for an invite, the calendar event id. */
  | { kind: 'session' | 'event' | 'series'; id: string }
  /** Offering by Service id: `/admin/events/:id` when `sharedEvent`, else `/admin/services/:id`. */
  | { kind: 'service'; id: string; sharedEvent: boolean }
  /** `/admin/forms/:formId/submissions?submissionId=:id`. */
  | { kind: 'form-submission'; id: string; formId: string }
  /** `/admin/clients/:id/forms`; `id` is the thread's user id. */
  | { kind: 'forms-section'; id: string };
export type ThreadTapTargetKind = ThreadTapTarget['kind'];

/**
 * chat-relay-context D8: what a sheet over the thread reads when the entry is tapped. `submission`: a
 * submitted form (`GET .../chat/submissions/:id`); `request`: a form request's waiting state
 * (`GET .../chat/requests/:rowId`); `payment`: a booking's payment (`GET .../chat/bookings/:bookingId/payment`).
 */
export type ThreadSheetRef =
  | { kind: 'submission'; id: string }
  | { kind: 'request'; rowId: string }
  | { kind: 'payment'; bookingId: string };

/** The state line under the message's subject (D7). */
export type AutomaticMessageState =
  | { kind: 'form-request'; status: 'awaiting' | 'submitted' | 'expired' | 'withdrawn'; submittedAt: string | null }
  | { kind: 'invite'; status: 'booked' | 'pending' | 'passed' };

/** A client-written text message. `text` is literal: escape on render, never parse as markup (D10). */
export interface ChatMessagePayload {
  text: string;
}

/** A non-text message (D5). `mediaType` is the Bot API kind, e.g. `photo`, `voice`, `video_note`. */
export interface ChatMediaPayload {
  mediaType: string;
}

/** One preparation page folded under its reminder (D9); never a top-level entry. */
export interface PreparationPageEntry {
  id: string;
  ordinal: number;
  /** Display-safe form of the stored copy (D10); null when the page has no stored copy. */
  text: string | null;
  parseMode: ThreadParseMode;
  at: string;
  deliveryState: ThreadDeliveryState;
}

/** A `CommunicationLog` send rendered as an automatic message (D7). */
export interface AutomaticMessagePayload {
  sendType: ClientIntentType;
  /** Display-safe form of `metadata.sentMessage.text` (D10); null when the row has no stored copy. */
  text: string | null;
  /** The stored copy's parse mode, kept for the record; the screen renders `text` as converted. */
  parseMode: ThreadParseMode;
  /** From `sendTypeAppearance`; rendered in place of the text when `text` is null. */
  label: string;
  state: AutomaticMessageState | null;
  target: ThreadTapTarget | null;
  /** Hydrated pages of a `pre_session_reminder`, ordered by ordinal; null for every other type. */
  preparation: PreparationPageEntry[] | null;
  /**
   * chat-relay-context D8: the sheet this message opens, or null (open `target` instead). A form request
   * (or its reminder) opens its bound submission when submitted, else the request's waiting state; a
   * legacy submitted request with no bound submission has none. A money notice (payment completed,
   * refund, a confirmation stating Paid) opens its booking's payment.
   */
  sheet: ThreadSheetRef | null;
}

/**
 * chat-relay-context D3: an event that reached the client without a message, drawn as a margin-rail line.
 * `form_submitted`: a submission no request message shows; `payment`: a card payment no confirmation
 * states; `marked_paid`: a payment the business recorded; `refund`: money returned that no refund
 * notice in the thread shows.
 */
export type ThreadActivityKind = 'form_submitted' | 'payment' | 'marked_paid' | 'refund';

export interface ActivityPayload {
  activity: ThreadActivityKind;
  /** Money lines: the amount in cents (a refund line carries only what no notice covers); null for a submission. */
  amountCents: number | null;
  /** Submission lines: the form's name as submitted; null for money lines. */
  formName: string | null;
  /**
   * The booking's service name at read time: a money line's booking; a submission line's bound booking
   * (its `bookingId`), so the screen says "while booking" only when there is a booking to open. Null
   * when unbound or gone.
   */
  serviceName: string | null;
  /**
   * Money lines: how the money moved, lowercase, worded as the session page's payment story: the
   * booking's payment method (`cash`, `venmo`, `zelle`, `card`, `other`) when recorded, else `card`
   * for Stripe and `manual` for a manual record. A refund line: `card` for a Stripe refund, the
   * booking's method (else `manual`) for a manual one. Null for a submission.
   */
  method: string | null;
  /** The sheet View opens (D8): the submission, or the booking's payment. */
  sheet: ThreadSheetRef;
  /** The page behind the sheet: the submission page, or the booking's calendar page; null when gone. */
  target: ThreadTapTarget | null;
}

/** An admin reply (chat-relay-reply D8): the admin's own words, literal, never markup. */
export interface ReplyPayload {
  text: string;
  /** `CommunicationLog.failureReason` when the row is failed or suppressed; null otherwise. */
  failureReason: string | null;
}

interface ThreadEntryBase {
  /** `ClientChatMessage.id` or `CommunicationLog.id`; stable, so the screen replaces by id. */
  id: string;
  channel: ThreadChannel;
  direction: ThreadDirection;
  /** ISO time: chat `createdAt`; ledger display time `COALESCE(sentAt, lastAttemptAt, failedAt, createdAt)`. */
  at: string;
}

export type ThreadEntry =
  /**
   * client-chat-screen D3: a `secure` row carries its sender's `idempotencyKey` (the composer's key,
   * so the screen reconciles its optimistic bubble by it), and a business-sent `secure` row carries
   * `deliveryState: 'sent'` from creation (stored is its delivery truth; Seen comes from the
   * response's `secureRead`, never from the entry).
   */
  | (ThreadEntryBase & {
    kind: 'chat-message';
    seq: number;
    payload: ChatMessagePayload;
    idempotencyKey?: string;
    deliveryState?: ThreadDeliveryState;
  })
  | (ThreadEntryBase & { kind: 'chat-media'; seq: number; payload: ChatMediaPayload })
  | (ThreadEntryBase & { kind: 'automatic'; payload: AutomaticMessagePayload; deliveryState: ThreadDeliveryState })
  /**
   * chat-relay-context D3: an activity line. `id` is `activity:<source>:<id>` (stable across refreshes);
   * `channel` is `secure` and `direction` names who acted (inbound: the client submitted or paid;
   * outbound: the business marked paid or refunded). Ordered as a ledger-source entry everywhere,
   * tiebroken by id, like a reply.
   */
  | (ThreadEntryBase & { kind: 'activity'; payload: ActivityPayload })
  /**
   * chat-relay-reply D8: a `CommunicationLog` row of type `chat`. Carries the conversation sequence
   * number (`metadata.chatSeq`) AND delivery state; `idempotencyKey` is the composer's key, so the
   * screen reconciles its optimistic bubble by it (D10). `uncertain`: the attempts ended without
   * Telegram confirming the outcome (`lastAttemptOutcome` UNCERTAIN), shown as such, never as proven
   * non-delivery (D4). `retryable`: exactly the retry mutation's guard (FAILED and not revoked), so a
   * suppressed or revoked reply never offers a retry the server refuses (D3). Ordered as a
   * ledger-source entry everywhere, tiebroken by id (D8).
   */
  | (ThreadEntryBase & {
    kind: 'reply';
    seq: number;
    idempotencyKey: string;
    deliveryState: ThreadDeliveryState;
    uncertain: boolean;
    retryable: boolean;
    payload: ReplyPayload;
  });
export type ThreadEntryKind = ThreadEntry['kind'];

/** `GET /api/admin/clients/:id/chat` query. */
export interface ThreadQuery {
  /** client-chat-screen D4: the room read; `telegram` when omitted. `filter` is refused with `secure`. */
  room?: ThreadRoom;
  filter?: ThreadFilter;
  /** Opaque keyset cursor from a previous response; pages toward older history. */
  cursor?: string;
  /** The highest `throughSeq` the screen holds; returns chat messages above it, unfiltered. */
  afterSeq?: number;
  /** Previous response's `serverTime` minus 60 seconds; returns the ledger refresh. */
  ledgerSince?: string;
  /** chat-relay-context D6: same value as `ledgerSince`; returns the activity refresh. */
  activitySince?: string;
  /** Next unread catch-up page: unread client messages numbered above this seq. */
  catchUpAfterSeq?: number;
}

/**
 * client-chat-screen D3: the CLIENT participant's Secure marker, the admin's Seen. Every business-sent
 * secure entry with `seq` at or below `seq` is Seen; `at` is when the marker last moved (null: never).
 */
export interface SecureReadMarkerDto {
  seq: number;
  at: string | null;
}

/**
 * The person the thread belongs to (D1 predicate: a client or a visitor), for the screen header.
 * The client-detail read refuses visitors, so the thread carries its own.
 */
export interface ThreadPerson {
  /** User id (the route `:id`). */
  id: string;
  /** Full name, else `@username`, else "Client". */
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  username: string | null;
  avatarUrl: string | null;
  /** ISO time the person's record was created. */
  createdAt: string;
  /** False for a visitor: the header is not a profile link and back returns to Visitors (D8). */
  isRegistered: boolean;
}

/** `GET /api/admin/clients/:id/chat` response. */
export interface ThreadResponse {
  /** client-chat-screen D4: the room this response reads; it never carries or acknowledges the other room. */
  room: ThreadRoom;
  /** The thread's person, on every response. */
  person: ThreadPerson;
  /** client-chat-screen D6: the business's unread per room (the tab counts), as of this response. */
  unread: ThreadRoomCounts;
  /** client-chat-screen D3: the client's Secure marker; present on every `secure` response, never on `telegram`. */
  secureRead?: SecureReadMarkerDto;
  /** History page (or the `afterSeq` delta), ascending by time. */
  entries: ThreadEntry[];
  /** Unread client messages the page did not contain, ascending by seq, never filtered (D1). */
  catchUp: ThreadEntry[];
  /** True when the catch-up hit its cap; the screen fetches the rest before it acknowledges. */
  catchUpHasMore: boolean;
  /** Older history cursor; null when no older entries exist under the filter. */
  cursor: string | null;
  /** The conversation's `lastSeq` read first; every chat read is bounded by it. 0 with no conversation. */
  snapshotSeq: number;
  /** Always equal to `snapshotSeq`; the screen's next `afterSeq` and read acknowledgement. */
  throughSeq: number;
  /** ISO server time of this response (the basis of the next `ledgerSince` and `activitySince`). */
  serverTime: string;
  /** Present only when `ledgerSince` was sent (D9). */
  ledger?: {
    upserts: ThreadEntry[];
    /** Ledger entry ids that no longer qualify. */
    removals: string[];
  };
  /**
   * Present only when `activitySince` was sent (chat-relay-context D6): activity lines whose source
   * changed, under the active lens, and ids of lines that a changed ledger row now covers or whose
   * source no longer holds the event. Applied like the ledger refresh: removals, then upserts by id.
   */
  activity?: {
    upserts: ThreadEntry[];
    removals: string[];
  };
}

/** `POST /api/admin/clients/:id/chat/read` body (D3). */
export interface ThreadReadBody {
  throughSeq: number;
  /** client-chat-screen D4: the room whose marker moves; `telegram` when omitted. */
  room?: ThreadRoom;
}

/** `POST /api/admin/clients/:id/chat/read` response. */
export interface ThreadReadResponse {
  room: ThreadRoom;
  /** The room's marker after the read. */
  lastReadSeq: number;
  /** The business's unread across both rooms; each room's notice ends with its own room (chat-notice-split-by-room). */
  unreadCount: number;
  /** client-chat-screen D6: the business's unread per room after the read (the tab counts). */
  unread: ThreadRoomCounts;
}

/** `POST /api/admin/clients/:id/chat/messages` body (chat-relay-reply D1). */
export interface ReplySendBody {
  /** The admin's words; 1 to 4096 characters after trimming. Stored and sent trimmed, literal. */
  text: string;
  /** A UUID the composer generates once per tap and reuses on a lost response (D10). */
  idempotencyKey: string;
  /**
   * client-chat-screen D3: `telegram` (default) sends through the bookings bot; `secure` stores a
   * business-sent secure message and pings the client, never sending the words through Telegram.
   */
  channel?: ThreadChannel;
}

/** `POST /api/admin/clients/:id/chat/messages` response (201). */
export interface ReplySendResponse {
  /**
   * The reply entry: kind `reply` for `telegram`; kind `chat-message` (outbound, `secure`, with its
   * `idempotencyKey` and `deliveryState: 'sent'`) for `secure`. A repeated key returns the existing row.
   */
  entry: ThreadEntry;
  /** The conversation's `lastSeq` under the lock that wrote (or found) the reply. */
  snapshotSeq: number;
  /** True when this reply created the person's conversation; the screen refreshes so its stream rebinds (D11). */
  conversationCreated: boolean;
}

/** `POST /api/admin/clients/:id/chat/messages/:rowId/retry` response (D3). */
export interface ReplyRetryResponse {
  /** The same reply row, re-read after the reset (sending, unless delivery already moved it). */
  entry: ThreadEntry;
}

/**
 * `GET /api/admin/clients/:id/chat/requests/:rowId` response (chat-relay-context D8): a form request's
 * waiting state for the sheet. `rowId` may name a reminder; the read resolves it to its source request.
 */
export interface ThreadRequestSheet {
  /** The source request's row id: the one the revoke withdraws. */
  requestId: string;
  sendType: 'form_request' | 'follow_up';
  formId: string | null;
  /** The form's current name; null when the form no longer exists. */
  formName: string | null;
  status: 'awaiting' | 'submitted' | 'expired' | 'withdrawn';
  /** When the request went out (`sentAt`, else created). */
  sentAt: string;
  /** The latest sent reminder for it; null when none went out. */
  reminderSentAt: string | null;
  /** The fixed 30-day expiry off `sentAt`. */
  expiresAt: string;
  submittedAt: string | null;
  /** The bound submission (`metadata.submissionId`) when submitted and owned by this client; null otherwise. */
  submissionId: string | null;
  /** The page of the booking the request is attached to; null when none or gone. */
  booking: ThreadTapTarget | null;
  /** The attached booking's (or session's) service name; null when none or gone. */
  bookingServiceName: string | null;
  /** Exactly the revoke's guard: an awaiting request can be withdrawn. */
  canWithdraw: boolean;
}

/** One `Refund` action on the booking, newest first. Amounts are dollars, as the payment story. */
export interface ThreadPaymentSheetRefund {
  id: string;
  amount: number;
  provider: string;
  status: string;
  createdAt: string;
  completedAt: string | null;
}

/**
 * `GET /api/admin/clients/:id/chat/bookings/:bookingId/payment` response (chat-relay-context D8): the
 * booking's payment fields, named and valued as the session page's payment story (dollars).
 */
export interface ThreadPaymentSheet {
  bookingId: string;
  /** What it was for; null when the service is gone. */
  serviceName: string | null;
  scheduledAt: string;
  bookingStatus: string;
  bookedPrice: number | null;
  paymentStatus: string;
  paymentProgressStatus: string;
  paymentProvider: string | null;
  paymentMethod: string | null;
  paymentMethodNote: string | null;
  cardBrand: string | null;
  cardLast4: string | null;
  paidAt: string | null;
  paymentAmount: number | null;
  balanceDue: number | null;
  refundedAt: string | null;
  /** Cumulative dollars refunded. */
  refundAmount: number | null;
  disputedAt: string | null;
  refunds: ThreadPaymentSheetRefund[];
  /** Open booking: the booking's calendar page; null when gone. */
  target: ThreadTapTarget | null;
}

// ---------------------------------------------------------------------------
// admin-chats-list D1 and chat-unread-surfaces D1: the conversations list and the unread total.
// ---------------------------------------------------------------------------

/** `GET /api/admin/chats` query. */
export interface ChatsListQuery {
  /** The previous page's `nextCursor`; omitted for the newest page. */
  cursor?: string;
  /** Rows per page, default 30, at most 100. */
  limit?: number;
  /** Name search: first name, last name or username contains it, case-insensitively. */
  q?: string;
}

/** The person a list row belongs to (the thread predicate: a client or a visitor). */
export interface ChatsListPerson {
  /** User id: the thread route's `:id`. */
  id: string;
  /** Full name, else `@username`, else "Client" (the thread header's rule). */
  displayName: string;
  firstName: string | null;
  lastName: string | null;
  avatarUrl: string | null;
  /** False for a visitor (the row's Visitor mark). */
  isRegistered: boolean;
}

/**
 * The row's last line: the newest conversational message by its message time (a client message's
 * send time, a reply's creation time; `seq`, then id, break ties), never simply the one holding
 * `lastSeq`. Automatic sends are never a last line (admin-chats-list D7).
 */
export type ChatsListLastLine =
  /** The client spoke last; `text` is null for a media placeholder ("Sent media."). */
  | { kind: 'client'; text: string | null; sentAt: string }
  /** The admin's reply ("You:"), with its delivery state mapped as the thread maps it. */
  | { kind: 'you'; text: string; sentAt: string; deliveryState: ThreadDeliveryState };

export interface ChatsListRow {
  conversationId: string;
  person: ChatsListPerson;
  /** ISO time of the conversation's last conversational message (the sort key). */
  lastMessageAt: string;
  /** Client messages above the business read marker of their room, summed over both rooms (client-chat-screen D8). */
  unreadCount: number;
  /** Null only when no conversational message can be shown (never expected in practice). */
  last: ChatsListLastLine | null;
  /** client-chat-screen D8: the last line is a secure message (the lock before the preview). */
  secure: boolean;
  /** client-chat-screen D6: the room the row opens (newest unread client message, else newest message, else Telegram). */
  openRoom: ThreadRoom;
}

/** `GET /api/admin/chats` response: newest `lastMessageAt` first, then id. */
export interface ChatsListResponse {
  items: ChatsListRow[];
  /** Cursor for the next (older) page; null on the last page. */
  nextCursor: string | null;
}

/** `GET /api/admin/chats/unread` response (chat-unread-surfaces D1). */
export interface ChatUnreadTotalResponse {
  /** Unread client messages across every direct conversation of the business. */
  total: number;
}

// ---------------------------------------------------------------------------
// client-chat-screen D4, D7, D11: the client's own chat screen (`/api/webapp/chat/*`). The person is
// always the hub session's user; nothing here names a conversation, a business marker or a delivery state.
// ---------------------------------------------------------------------------

/**
 * History lenses (D4): All; Chat (the client's Telegram messages and the business's Telegram
 * replies); Bookings (the automatic lens minus forms rows, money notices and the welcome
 * acknowledgment, which shows under All only: booking notices, moves,
 * reminders with their folded pages, follow-ups, invites); Forms; Payments. The Secure room has none.
 */
export const CLIENT_THREAD_FILTERS = ['all', 'chat', 'bookings', 'forms', 'payments'] as const;
export type ClientThreadFilter = (typeof CLIENT_THREAD_FILTERS)[number];

/**
 * The one door a History notification's footer opens in the Mini App (D4). `path` is a client route:
 * `booking` `/booking-hub/booking/:bookingId`; `form` `/form-request/:ctaToken` (only while the request
 * waits); `invite` `/book/event/:calendarEventId` (only while an event invite is open; a session
 * invite has no door); `offering` `/book/service/:serviceId`, or `/book` with `sharedEvent` for a shared
 * event, which has no service page (the Book catalog, opened on its Events tab; web-account-pages). A
 * subject that is gone or consumed has none.
 */
export type ClientDoor =
  | { kind: 'booking'; path: string }
  | { kind: 'form'; path: string }
  | { kind: 'invite'; path: string }
  | { kind: 'offering'; path: string; sharedEvent?: true };
export type ClientDoorKind = ClientDoor['kind'];

/** A preparation page folded under its reminder, as the client received it. */
export interface ClientPreparationPage {
  id: string;
  ordinal: number;
  /**
   * Display-safe form of the stored copy's notes: the heading the first page opened with (the label,
   * the service and its date) is dropped, since the fold sits under its own message (Gabriel 2026-10-23).
   * Null when the page has no stored copy.
   */
  text: string | null;
  parseMode: ThreadParseMode;
  at: string;
}

/** A notification the bot confirmed delivered, as the client received it, buttons dropped (D4). */
export interface ClientAutomaticPayload {
  sendType: ClientIntentType;
  /** Display-safe form of the delivered copy; null when the row has no stored copy. */
  text: string | null;
  parseMode: ThreadParseMode;
  /** Rendered in place of the text when `text` is null. */
  label: string;
  /** A form request's or an invite's state line (Waiting for you, Submitted with the date, Expired, Withdrawn). */
  state: AutomaticMessageState | null;
  clientDoor: ClientDoor | null;
  /** Delivered pages of a `pre_session_reminder`, ordered by ordinal; null for every other type. */
  preparation: ClientPreparationPage[] | null;
  /** The heading the studio gave its notes (How to prepare unless it named its own), for the fold; null or absent without one. */
  preparationLabel?: string | null;
}

interface ClientThreadEntryBase {
  id: string;
  channel: ThreadChannel;
  /** `inbound`: the client wrote it (the screen's own side); `outbound`: the business or its bot. */
  direction: ThreadDirection;
  at: string;
}

/** One entry of the client's screen. No delivery state, failure reason or retry flag on anything (D4). */
export type ClientThreadEntry =
  /** A text message; a `secure` row carries its sender's `idempotencyKey` (the client's own send reconciles by it). */
  | (ClientThreadEntryBase & { kind: 'chat-message'; seq: number; payload: ChatMessagePayload; idempotencyKey?: string })
  /** Media the client sent through Telegram, shown as a quiet line. */
  | (ClientThreadEntryBase & { kind: 'chat-media'; seq: number; payload: ChatMediaPayload })
  /** The business's delivered Telegram reply. */
  | (ClientThreadEntryBase & { kind: 'reply'; seq: number; payload: { text: string } })
  | (ClientThreadEntryBase & { kind: 'automatic'; payload: ClientAutomaticPayload });
export type ClientThreadEntryKind = ClientThreadEntry['kind'];

/** `GET /api/webapp/chat` query. */
export interface ClientThreadQuery {
  /** `secure` (the screen's first tab) or `telegram` (History); `secure` when omitted. */
  room?: ThreadRoom;
  /** History lens; refused with `room=secure`. */
  filter?: ClientThreadFilter;
  cursor?: string;
  afterSeq?: number;
  catchUpAfterSeq?: number;
  /** History only: the previous `serverTime` minus 60 seconds; returns the ledger refresh. */
  ledgerSince?: string;
}

/** `GET /api/webapp/chat` response. Same cursor and snapshot rules as the admin thread. */
export interface ClientThreadResponse {
  room: ThreadRoom;
  /** History page (or the `afterSeq` delta), ascending by time. */
  entries: ClientThreadEntry[];
  /** Business messages of this room above the client's marker that the page did not contain. */
  catchUp: ClientThreadEntry[];
  catchUpHasMore: boolean;
  cursor: string | null;
  snapshotSeq: number;
  /** Always equal to `snapshotSeq`; the next `afterSeq` and the read acknowledgement for this room. */
  throughSeq: number;
  serverTime: string;
  /** Business-sent secure messages the client has not read (the Home chip's badge). */
  secureUnread: number;
  /** History only, present when `ledgerSince` was sent. */
  ledger?: { upserts: ClientThreadEntry[]; removals: string[] };
}

/** `POST /api/webapp/chat/read` body. */
export interface ClientChatReadBody {
  room: ThreadRoom;
  throughSeq: number;
}

/** `POST /api/webapp/chat/read` response. */
export interface ClientChatReadResponse {
  room: ThreadRoom;
  /** The client's marker for the room after the read. */
  lastReadSeq: number;
  secureUnread: number;
}

/** `POST /api/webapp/chat/messages` body (D2). */
export interface ClientChatSendBody {
  /** 1 to 4096 characters after trimming. */
  text: string;
  /** A UUID generated once per tap and reused on a lost response. */
  idempotencyKey: string;
}

/** `POST /api/webapp/chat/messages` response (201): the stored message; a repeated key returns the same row. */
export interface ClientChatSendResponse {
  entry: ClientThreadEntry;
  snapshotSeq: number;
  /** True when this send created the conversation; the screen reopens its stream so it binds. */
  conversationCreated: boolean;
}

/**
 * D11: a typing event on the conversation channel, forwarded by both event streams as `event: typing`.
 * `from` is derived from the authenticated route; `until` is an ISO time five seconds after the post.
 */
export interface ChatTypingEvent {
  type: 'typing';
  from: 'business' | 'client';
  room: 'secure';
  until: string;
}
